import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { clientService } from './clientService';

const COLLECTION = 'bookings';
const NOTIFICATIONS_COLLECTION = 'notifications';

// Helper to create a notification for admin
const createNotification = async (bookingData, bookingId, isNewClient) => {
  try {
    const notificationData = {
      type: 'new_booking',
      title: 'New Booking Received',
      message: `${bookingData.clientName} booked ${bookingData.serviceName} for ${bookingData.date} at ${bookingData.time}`,
      bookingId: bookingId,
      clientId: bookingData.clientId,
      clientName: bookingData.clientName,
      clientEmail: bookingData.clientEmail,
      serviceName: bookingData.serviceName,
      date: bookingData.date,
      time: bookingData.time,
      price: bookingData.price,
      isNewClient: isNewClient,
      source: bookingData.source,
      read: false,
      readBy: [],
      createdAt: serverTimestamp()
    };
    await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notificationData);
  } catch (err) {
    console.error('Error creating notification:', err);
  }
};

export const bookingService = {
  async createFromContactForm(formData) {
    const { client, isNew } = await clientService.findOrCreate({
      name: formData.name,
      email: formData.email,
      phone: formData.phone
    });

    const bookingData = {
      clientId: client.id,
      clientName: formData.name,
      clientEmail: formData.email.toLowerCase(),
      clientPhone: formData.phone || '',
      serviceId: null,
      serviceName: formData.service,
      date: null,
      time: null,
      duration: null,
      price: null,
      status: 'pending',
      location: 'TBD',
      notes: formData.message || '',
      source: 'website_contact_form',
      isNewClient: isNew,
      // Assignment fields
      assignedTo: null,
      assignedToName: null,
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION), bookingData);

    // Create notification for admin
    await createNotification(bookingData, docRef.id, isNew);

    // Log to security logs for monitoring
    try {
      await addDoc(collection(db, 'securityLogs'), {
        action: 'create',
        collection: 'bookings',
        documentId: docRef.id,
        userId: null,
        userEmail: formData.email.toLowerCase(),
        before: null,
        after: {
          serviceName: formData.service,
          status: 'pending',
          source: 'website_contact_form',
          isNewClient: isNew
        },
        changedFields: null,
        recaptchaScore: null,
        ip: null,
        userAgent: navigator.userAgent,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.error('Error logging to securityLogs:', err);
    }

    return {
      booking: { id: docRef.id, ...bookingData },
      client,
      isNewClient: isNew
    };
  },

  async createBooking(formData) {
    const { client, isNew } = await clientService.findOrCreate({
      name: formData.name,
      email: formData.email,
      phone: formData.phone
    });

    // Calculate end time based on duration
    const calculateEndTime = (startTime, duration) => {
      if (!startTime || !duration) return null;
      const [hours, minutes] = startTime.split(':').map(Number);
      const durationMatch = duration.match(/(\d+)/);
      const durationMinutes = durationMatch ? parseInt(durationMatch[1]) : 60;
      const totalMinutes = hours * 60 + minutes + durationMinutes;
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
    };

    // Convert date string to Date object for Firestore
    // Uses noon (12:00) instead of midnight to prevent off-by-one day errors
    // when the local timezone (AEST UTC+10/+11) converts to UTC in Firestore
    const parseDate = (dateStr) => {
      if (dateStr instanceof Date) return dateStr;
      if (typeof dateStr === 'string') {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day, 12, 0, 0);
      }
      return new Date(dateStr);
    };

    const bookingData = {
      clientId: client.id,
      clientName: formData.name,
      clientEmail: formData.email.toLowerCase(),
      clientPhone: formData.phone || '',
      serviceId: formData.serviceId,
      serviceName: formData.service,
      date: parseDate(formData.date),
      dateString: formData.date, // Keep string version for display
      time: formData.time,
      endTime: calculateEndTime(formData.time, formData.duration),
      duration: formData.duration,
      price: formData.price,
      status: 'pending', // Start as pending for admin review
      location: 'Studio',
      notes: formData.message || '',
      source: 'website_booking_form',
      isNewClient: isNew,
      // Assignment fields
      assignedTo: null,
      assignedToName: null,
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Status history for tracking
      statusHistory: [{
        status: 'pending',
        timestamp: new Date().toISOString(),
        note: 'Booking created from website'
      }]
    };

    const docRef = await addDoc(collection(db, COLLECTION), bookingData);

    // Update client stats
    try {
      await updateDoc(doc(db, 'clients', client.id), {
        totalBookings: (client.totalBookings || 0) + 1,
        totalAppointments: (client.totalAppointments || 0) + 1,
        lastVisit: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Remove 'New' tag if this is a returning client
        ...(isNew ? {} : { tags: client.tags?.filter(t => t !== 'New') || [] })
      });
    } catch (err) {
      console.error('Error updating client stats:', err);
    }

    // Create notification for admin
    await createNotification(bookingData, docRef.id, isNew);

    // Log to security logs for monitoring
    try {
      await addDoc(collection(db, 'securityLogs'), {
        action: 'create',
        collection: 'bookings',
        documentId: docRef.id,
        userId: null,
        userEmail: formData.email.toLowerCase(),
        before: null,
        after: {
          serviceName: formData.service,
          status: 'pending',
          source: 'website_booking_form',
          isNewClient: isNew,
          date: formData.date,
          time: formData.time
        },
        changedFields: null,
        recaptchaScore: null,
        ip: null,
        userAgent: navigator.userAgent,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.error('Error logging to securityLogs:', err);
    }

    return {
      booking: { id: docRef.id, ...bookingData },
      client,
      isNewClient: isNew
    };
  },

  // Update booking (e.g., to link invoiceId)
  async update(bookingId, data) {
    const bookingRef = doc(db, COLLECTION, bookingId);
    await updateDoc(bookingRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  }
};

export default bookingService;
